import React from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useTable, useSortBy, usePagination } from 'react-table';
import {
  StyledTable,
  StyledTrTh,
  StyledThead,
  StyledTh,
  StyledTbody,
  StyledTd,
  StyledTrTd,
  StyledPagination,
  ArrowRight,
  ArrowLeft,
  StyledPaginationButton,
  StyledPageNumber,
  StyledPageLabel,
  StyledSelectContainer,
  StyledSelect,
  StyledTableContainer,
  StyledDealsPlayerName,
  StyledDealStatus,
  StyledStatusFilter,
  StyledStatusChip,
  StyledMakeOfferButton,
} from './styled';
import { transferShowUnshowFilter } from '../../../../../../redux/actions/Transfers';
import { teamAddToList } from '../../../../../../redux/actions/Team';
import { sidebarOpenRightPanel } from '../../../../../../redux/actions/Sidebar';
import { numberWithCommas } from '../../../../../../service/utils';

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const BanIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m4.9 4.9 14.2 14.2" />
  </svg>
);

const DEAL_STATUS_META = {
  Accepted: { color: '#00a272', Icon: CheckIcon },
  Rejected: { color: '#de3e32', Icon: XIcon },
  Canceled: { Icon: BanIcon },
};

export const STATUS_FILTERS = ['All', 'Accepted', 'Rejected', 'Canceled'];

const DealStatus = ({ label }) => {
  const meta = DEAL_STATUS_META[label];
  if (!meta) {
    return label ? <>{label}</> : null;
  }
  const { color, Icon } = meta;
  return (
    <StyledDealStatus $color={color}>
      <Icon />
      {label}
    </StyledDealStatus>
  );
};

const canMakeOffer = (row) =>
  row.status === 'Rejected' &&
  (row.dealType === 'Buy' || row.dealType === 'Loan In');

const TableContainer = ({ columns, data, onMakeOffer }) => {
  const [statusFilter, setStatusFilter] = React.useState('All');

  const filteredData = React.useMemo(() => {
    if (statusFilter === 'All') return data;
    return data.filter((row) => row.status === statusFilter);
  }, [data, statusFilter]);

  const showOptions = React.useMemo(
    () => [
      { value: 10, label: '10' },
      { value: 20, label: '20' },
      { value: 50, label: '50' },
      { value: 100, label: '100' },
      { value: 'all', label: 'All' },
    ],
    []
  );

  const [tableSize, setTableSize] = React.useState(showOptions[0]);

  const resolvePageSize = React.useCallback(
    (option, rows) => {
      if (option?.value === 'all') {
        return Math.max(rows.length, 1);
      }
      return option?.value ?? 10;
    },
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: filteredData,
      autoResetSortBy: false,
      autoResetPage: false,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [{ id: 'date', desc: true }],
      },
    },
    useSortBy,
    usePagination
  );

  const dispatch = useDispatch();

  React.useEffect(() => {
    gotoPage(0);
    setPageSize(resolvePageSize(tableSize, filteredData));
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps -- reset only when filter changes

  React.useEffect(() => {
    if (tableSize.value === 'all') {
      setPageSize(Math.max(filteredData.length, 1));
    }
  }, [filteredData.length, tableSize, setPageSize]);

  const setRowSize = (e) => {
    setTableSize(e);
    setPageSize(resolvePageSize(e, filteredData));
  };

  const openSidebarWithPlayer = (playerId) => {
    dispatch(transferShowUnshowFilter(false));
    dispatch(sidebarOpenRightPanel(true));
    dispatch(teamAddToList({ _id: playerId }));
  };

  const renderCell = (cell) => {
    const row = cell.row.original;
    switch (cell.column.id) {
      case 'date':
        return (
          <span>
            {moment(row.date).local().format('D MMM YYYY')}
          </span>
        );

      case 'dealType':
        return row.dealType;

      case 'playerName':
        return (
          <StyledDealsPlayerName
            onClick={() => openSidebarWithPlayer(row.playerId)}
          >
            {row.playerName}
          </StyledDealsPlayerName>
        );

      case 'amount':
        return `$${numberWithCommas(row.amount)}`;

      case 'status':
        return <DealStatus label={row.status} />;

      case 'action':
        if (!canMakeOffer(row) || !onMakeOffer) return null;
        return (
          <StyledMakeOfferButton
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onMakeOffer(row);
            }}
          >
            Make offer
          </StyledMakeOfferButton>
        );

      default:
        return cell.render('Cell');
    }
  };

  return (
    <>
      <StyledStatusFilter role="tablist" aria-label="Deal status">
        {STATUS_FILTERS.map((label) => (
          <StyledStatusChip
            key={label}
            type="button"
            role="tab"
            aria-selected={statusFilter === label}
            $active={statusFilter === label}
            onClick={() => setStatusFilter(label)}
          >
            {label}
          </StyledStatusChip>
        ))}
      </StyledStatusFilter>
      <StyledTableContainer>
        <StyledTable {...getTableProps()}>
          <StyledThead>
            {headerGroups.map((headerGroup) => (
              <StyledTrTh {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <StyledTh type={column.Header} {...column.getHeaderProps()}>
                    {column.render('Header')}
                  </StyledTh>
                ))}
              </StyledTrTh>
            ))}
          </StyledThead>
          <StyledTbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <StyledTrTd
                  className={`row-number-${row.id}`}
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell) => (
                    <StyledTd
                      type={cell.column.Header}
                      statusCell={cell.column.id}
                      pointerCursor={cell.column.Header === 'Player Name'}
                      {...cell.getCellProps()}
                    >
                      {renderCell(cell)}
                    </StyledTd>
                  ))}
                </StyledTrTd>
              );
            })}
          </StyledTbody>
        </StyledTable>
      </StyledTableContainer>

      <StyledPagination>
        <StyledPageNumber>
          <StyledPaginationButton
            type="button"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            <ArrowLeft />
          </StyledPaginationButton>
          <StyledPageLabel>
            Page <strong>{pageIndex + 1}</strong> of{' '}
            {Math.max(pageOptions.length, 1)}
          </StyledPageLabel>
          <StyledPaginationButton
            type="button"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            <ArrowRight />
          </StyledPaginationButton>
        </StyledPageNumber>

        <StyledSelectContainer>
          <span>Showing deals: </span>
          <StyledSelect
            classNamePrefix="react-select"
            defaultValue={showOptions[0]}
            value={tableSize}
            options={showOptions}
            onChange={setRowSize}
            menuPlacement="auto"
          />
        </StyledSelectContainer>
      </StyledPagination>
    </>
  );
};

export default React.memo(TableContainer);
